import { type RefObject, useEffect, useRef } from 'react';

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface UseDialogFocusOptions {
  /** Whether the dialog is showing. Pass `true` for components that only mount while open. */
  isOpen: boolean;
  /** The dialog element (the one with role="dialog"). */
  containerRef: RefObject<HTMLElement>;
  /** Called on Escape. Omit for dialogs that must be answered (terms, onboarding). */
  onClose?: () => void;
  /** Element to focus first; defaults to the first tabbable inside, then the container itself. */
  initialFocusRef?: RefObject<HTMLElement>;
}

function tabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * useDialogFocus - the shared focus behaviour for every modal dialog:
 * focus moves in on open, Tab and Shift+Tab wrap inside, Escape closes (when `onClose` is given),
 * and focus returns to whatever opened the dialog when it closes.
 */
export function useDialogFocus({ isOpen, containerRef, onClose, initialFocusRef }: UseDialogFocusOptions): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const container = containerRef.current;
    if (container) {
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      const target = initialFocusRef?.current ?? tabbables(container)[0] ?? container;
      target.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const box = containerRef.current;
      if (!box) return;
      if (event.key === 'Escape' && onCloseRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = tabbables(box);
      if (items.length === 0) {
        event.preventDefault();
        box.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!box.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === box)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (opener && document.contains(opener)) opener.focus();
    };
    // Re-run only when the dialog opens or closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}
